import {
  OPT_IN_PREFERENCE,
  OptInPreference,
} from '@/constants/features/opt-in';
import { FLAG_CONFIG_KEYS, IFlagsConfig } from '@/models';

// Every checkbox any of the consent screens can show. A checkbox is one line the
// user ticks, and `grants` names the data-collection buckets that ticking it
// hands over. Which of these checkboxes a given screen shows is in variants.ts.
// Comments here shorten the clickstream bucket to cs, as the ids do.

export type ConsentCheckboxId =
  | 'affiliate'
  | 'thirdParty'
  | 'ai'
  | 'us-all-data'
  | 'tc-us'
  | 'tc-us-held'
  | 'tc-row'
  | 'all-buckets-legacy'
  | 'tc-legacy';

export type AllOrNothingGroupId = 'row-data';

// The two Rest-of-World legal statements whose acceptance is stored on its own
// flag, so a checkbox the user already accepted can be hidden next visit.
export const DISCLOSURE_FLAG = {
  AFFILIATE: FLAG_CONFIG_KEYS.CONSENT_AFFILIATES,
  THIRD_PARTY: FLAG_CONFIG_KEYS.CONSENT_THIRD_PARTIES,
} as const;

export type DisclosureFlagKey =
  (typeof DISCLOSURE_FLAG)[keyof typeof DISCLOSURE_FLAG];

export type StoredDisclosureFlags = Pick<IFlagsConfig, DisclosureFlagKey>;

// Buckets that land only once every visible checkbox of the group is ticked,
// and not at all otherwise. The RoW screen splits its ask into two separate
// legal statements (affiliates, third parties) and may only use the data if the
// user accepts both. cs is deliberately absent: the RoW terms checkbox grants it
// alone, so a terms-only submit is still a valid opt-in.
export const ALL_OR_NOTHING_GRANTS: Record<
  AllOrNothingGroupId,
  OptInPreference[]
> = {
  'row-data': [
    OPT_IN_PREFERENCE.SEARCH,
    OPT_IN_PREFERENCE.E_COMMERCE,
    OPT_IN_PREFERENCE.ADS,
    OPT_IN_PREFERENCE.AI,
  ],
};

// Two fields below read almost alike and mean different things: `requires` names
// other checkboxes to tick alongside this one, while `required` only decides
// whether the Continue button unlocks.
export interface IConsentCheckbox {
  grants: OptInPreference[];
  // This checkbox's buckets live in ALL_OR_NOTHING_GRANTS, not in `grants`, and
  // land only once every visible checkbox sharing the id is checked.
  allOrNothingGroup?: AllOrNothingGroupId;
  // Unlocks the Continue button, and nothing else. Not a way to ask "is this the
  // terms checkbox" — use isTerms for that.
  required: boolean;
  // This checkbox IS the terms acceptance: it reports as EULA and records eulaAgreed.
  isTerms?: boolean;
  // Ticking this checkbox stores the named flag as true, permanently.
  disclosureFlagKey?: DisclosureFlagKey;
  // Other checkboxes to tick along with this one. No checkbox uses it today; see
  // logic/checkbox-selection.ts.
  requires?: ConsentCheckboxId[];
  labelKey: string;
  // Terms checkboxes render prefix + external link + suffix instead of a single label.
  linkKeys?: { prefix: string; link: string; suffix: string };
}

const LEGACY_EULA_LINK_KEYS = {
  prefix: 'onboarding.dataConsent.eulaPrefix',
  link: 'onboarding.dataConsent.eulaLinkText',
  suffix: 'onboarding.dataConsent.eulaSuffix',
};

// Shared by tc-us and tc-us-held: same sentence, different grants.
const US_EULA_LINK_KEYS = {
  prefix: 'onboarding.dataConsent.rows.tcUs',
  link: 'onboarding.dataConsent.rows.tcUsLink',
  suffix: 'onboarding.dataConsent.rows.tcUsSuffix',
};

export const CONSENT_CHECKBOXES: Record<ConsentCheckboxId, IConsentCheckbox> = {
  affiliate: {
    grants: [],
    required: false,
    allOrNothingGroup: 'row-data',
    disclosureFlagKey: DISCLOSURE_FLAG.AFFILIATE,
    labelKey: 'onboarding.dataConsent.rows.affiliate',
  },
  thirdParty: {
    grants: [],
    required: false,
    allOrNothingGroup: 'row-data',
    disclosureFlagKey: DISCLOSURE_FLAG.THIRD_PARTY,
    labelKey: 'onboarding.dataConsent.rows.thirdParty',
  },
  // Shown by the us-cs-held screen only, whose copy covers AI data alone.
  ai: {
    grants: [OPT_IN_PREFERENCE.AI],
    required: false,
    labelKey: 'onboarding.dataConsent.rows.ai',
  },
  // Shown by the us-cs-missing screen only. Its copy covers all three legal data
  // categories, so the id reads broader than the grant: tc-us alongside it
  // already writes cs and the grouped buckets, leaving ai as all this can add.
  'us-all-data': {
    grants: [OPT_IN_PREFERENCE.AI],
    required: false,
    labelKey: 'onboarding.dataConsent.rows.usAllBuckets',
  },
  'tc-us': {
    grants: [
      OPT_IN_PREFERENCE.CLICK_STREAM,
      OPT_IN_PREFERENCE.SEARCH,
      OPT_IN_PREFERENCE.E_COMMERCE,
      OPT_IN_PREFERENCE.ADS,
    ],
    required: true,
    isTerms: true,
    labelKey: US_EULA_LINK_KEYS.prefix,
    linkKeys: US_EULA_LINK_KEYS,
  },
  // Grants nothing on purpose: the us-cs-held user already holds cs, and may have
  // switched the grouped buckets back off in Settings, so re-accepting the terms
  // must not quietly restore them.
  'tc-us-held': {
    grants: [],
    required: true,
    isTerms: true,
    labelKey: US_EULA_LINK_KEYS.prefix,
    linkKeys: US_EULA_LINK_KEYS,
  },
  'tc-row': {
    grants: [OPT_IN_PREFERENCE.CLICK_STREAM],
    required: true,
    isTerms: true,
    labelKey: 'onboarding.dataConsent.rows.tcRow',
    linkKeys: {
      prefix: 'onboarding.dataConsent.rows.tcRow',
      link: 'onboarding.dataConsent.rows.tcRowLink',
      suffix: 'onboarding.dataConsent.rows.tcRowSuffix',
    },
  },
  // legacy-unified only — its own copy keys, never shared with other variants.
  'all-buckets-legacy': {
    grants: [
      OPT_IN_PREFERENCE.CLICK_STREAM,
      OPT_IN_PREFERENCE.SEARCH,
      OPT_IN_PREFERENCE.E_COMMERCE,
      OPT_IN_PREFERENCE.ADS,
      OPT_IN_PREFERENCE.AI,
    ],
    required: false,
    labelKey: 'onboarding.dataConsent.consentStandard',
  },
  'tc-legacy': {
    grants: [],
    required: true,
    isTerms: true,
    labelKey: 'onboarding.dataConsent.eulaLinkText',
    linkKeys: LEGACY_EULA_LINK_KEYS,
  },
};
